import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  CORE_STRENGTHS,
  HIGH_SCHOOL_STEPS,
  PRIMARY_STEPS,
} from "../../../constants/programContent";
import { C, FONT, container, sectionTitle, sectionLead } from "../../../constants/mq";

// What makes CS different, laid out like "Why Macquarie": four line icons on
// white, then the 7 Step Learning System as white cards on a sand band.
// Nothing here sits on red.

const Icon = ({ name }) => {
  const p = { fill: "none", stroke: C.ink, strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" };
  const paths = {
    award: (
      <>
        <circle cx="36" cy="26" r="15" {...p} />
        <path d="M36 17.5l2.6 5.3 5.8.8-4.2 4.1 1 5.8L36 30.8l-5.2 2.7 1-5.8-4.2-4.1 5.8-.8z" {...p} />
        <path d="M27 38l-7 16 7-2 4 6 5-12M45 38l7 16-7-2-4 6-5-12" {...p} />
      </>
    ),
    book: (
      <>
        <path d="M14 18c8-3 15-3 22 2v36c-7-5-14-5-22-2z" {...p} />
        <path d="M58 18c-8-3-15-3-22 2v36c7-5 14-5 22-2z" {...p} />
      </>
    ),
    people: (
      <>
        <circle cx="36" cy="20" r="7" {...p} />
        <circle cx="20" cy="25" r="5.5" {...p} />
        <circle cx="52" cy="25" r="5.5" {...p} />
        <path d="M25 58V40c0-6 5-10 11-10s11 4 11 10v18z" {...p} />
        <path d="M21 56H12V42c0-5 3.5-8 8-8 2 0 3.6.5 5 1.4M51 56h9V42c0-5-3.5-8-8-8-2 0-3.6.5-5 1.4" {...p} />
      </>
    ),
    pencil: (
      <>
        <path d="M18 12h26l10 10v38H18z" {...p} />
        <path d="M44 12v10h10M25 32h22M25 40h22M25 48h14" {...p} />
      </>
    ),
  };
  return (
    <svg width="76" height="76" viewBox="0 0 72 72" aria-hidden="true">
      {paths[name]}
    </svg>
  );
};

const POINTS = [
  { icon: "award", title: "Since 2002", body: "Writing and teaching our own courses for over twenty years." },
  ...CORE_STRENGTHS.map((c, i) => ({ icon: ["book", "people", "pencil"][i], title: c.title, body: c.body })),
];

const TABS = [
  { key: "primary", label: "Primary", steps: PRIMARY_STEPS },
  { key: "high", label: "High School", steps: HIGH_SCHOOL_STEPS },
];

const css = `
.cs-why-points { display: grid; grid-template-columns: repeat(4, 1fr); gap: 32px; margin-top: 44px; }
.cs-why-point { text-align: center; }
.cs-why-point h3 { font-size: 14.5px; font-weight: 700; color: ${C.ink}; margin: 14px 0 8px; }
.cs-why-point p { font-size: 13.5px; line-height: 1.65; color: ${C.body}; margin: 0; }
.cs-steps { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-top: 28px; }
.cs-step { padding: 22px 20px 24px; }
.cs-step__no { font-size: 13px; font-weight: 800; color: ${C.bright}; letter-spacing: 1px; margin: 0 0 10px; }
.cs-step h4 { font-size: 15.5px; font-weight: 700; color: ${C.ink}; margin: 0 0 8px; line-height: 1.35; }
.cs-step p { font-size: 13.5px; line-height: 1.65; color: ${C.body}; margin: 0; }
@media (max-width: 960px) {
  .cs-why-points, .cs-steps { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 560px) {
  .cs-why-points, .cs-steps { grid-template-columns: 1fr; }
}
`;

const WhyCsSection = () => {
  // Primary and high school run different seven-step cycles, so the visitor
  // picks the one that applies to their child rather than reading both.
  const [tab, setTab] = useState("primary");
  const steps = TABS.find((t) => t.key === tab).steps;

  return (
    <>
      <style>{css}</style>
      <section style={{ background: "#ffffff", padding: "24px 0 84px", fontFamily: FONT }}>
        <div style={container}>
          <h2 style={sectionTitle}>Why CS Education</h2>
          <p style={sectionLead}>
            Coaching colleges all promise results. What decides them is the
            course, the teacher and the material in front of the student.
          </p>
          <div className="cs-why-points">
            {POINTS.map((pt) => (
              <div key={pt.title} className="cs-why-point">
                <Icon name={pt.icon} />
                <h3>{pt.title}</h3>
                <p>{pt.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ background: C.sand, padding: "76px 0 84px", fontFamily: FONT }}>
        <div style={container}>
          <h2 style={sectionTitle}>The 7 Step Learning System</h2>
          <p style={sectionLead}>
            Every student moves through seven steps each term. It is a cycle,
            not a list &mdash; the assessment that closes one term sets the
            starting point for the next.
          </p>

          <div style={{ display: "flex", justifyContent: "center", marginTop: 26 }}>
            <div className="mq-tabs">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`mq-tab${tab === t.key ? " mq-tab--on" : ""}`}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="cs-steps">
            {steps.map((st, i) => (
              <div key={st.name} className="mq-card mq-card--hover cs-step">
                <p className="cs-step__no">{String(i + 1).padStart(2, "0")}</p>
                <h4>{st.name}</h4>
                <p>{st.body}</p>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 32, marginTop: 34 }}>
            <Link className="mq-link" to="/why-cs">
              More about how we teach
            </Link>
            <Link className="mq-link" to="/our-courses">
              Browse courses
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default WhyCsSection;
